import { useState } from "react";
import { createRoute } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { Route as RootRoute } from "./__root";
import { forgotPasswordSchema, type ForgotPasswordInput } from "../schemas/login";
import { apiCall } from "../lib/apiClient";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";

/**
 * `/forgot-password` — anti-enumeration password reset request.
 *
 * REQ-AUTH-007: backend vrátí 200 vždy, bez ohledu na existenci e-mailu.
 * Frontend zobrazí jednotné "If account exists, you'll receive an email"
 * po úspěchu. Tlačítko se dočasně zablokuje (`isPending`).
 */
export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: "/forgot-password",
  component: ForgotPasswordComponent,
});

function ForgotPasswordComponent() {
  const [sent, setSent] = useState(false);
  const form = useForm<ForgotPasswordInput>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: "" },
  });
  const mutation = useMutation({
    mutationFn: (input: ForgotPasswordInput) =>
      apiCall<void>("/api/public/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => setSent(true),
    // I při chybě nezobrazujeme (anti-enum) — fallback success message po debounce.
    onError: () => setSent(true),
  });

  return (
    <main className="min-h-screen bg-primary px-4 py-12 sm:bg-secondary md:px-8 md:pt-24">
      <div className="flex w-full flex-col gap-6 bg-primary sm:mx-auto sm:max-w-110 sm:rounded-2xl sm:px-10 sm:py-8 sm:shadow-sm">
        <div className="flex flex-col gap-2">
          <h1 className="text-xl font-semibold text-primary md:text-display-xs">Zapomenuté heslo</h1>
          <p className="text-md text-tertiary">
            Zadejte e-mail a pošleme vám odkaz pro obnovu hesla.
          </p>
        </div>
        {sent ? (
          <InlineAlert variant="success" title="Pokud účet existuje">
            Odeslali jsme odkaz pro reset hesla. Zkontrolujte svou e-mailovou schránku.
          </InlineAlert>
        ) : (
          <form
            noValidate
            onSubmit={form.handleSubmit((v) => mutation.mutate(v))}
            className="flex flex-col gap-6"
          >
            <Input
              label="E-mail"
              type="email"
              autoComplete="email"
              size="lg"
              isRequired
              isInvalid={Boolean(form.formState.errors.email)}
              hint={form.formState.errors.email?.message ?? undefined}
              value={form.watch("email")}
              onChange={(v) => form.setValue("email", v, { shouldValidate: form.formState.isSubmitted })}
            />
            <Button
              type="submit"
              color="primary"
              size="lg"
              isLoading={mutation.isPending}
              isDisabled={mutation.isPending}
            >
              Odeslat
            </Button>
          </form>
        )}
        <p className="text-center text-sm text-tertiary">
          <Button color="link-color" size="md" href="/login">
            Zpět na přihlášení
          </Button>
        </p>
      </div>
    </main>
  );
}
